/**
 * @page QmFeedbackPage
 * @route /qm/feedback
 * @description QM Modul 5 (#920) — Manager-Dashboard fuer Voice/Text-Vorschlaege.
 *   Liste mit Filtern, Detail-Modal mit Audio-Player, Whisper-Transkript,
 *   editierbare Sonnet-Strukturierung und Aktionen (Annehmen/Ablehnen/Rueckfrage).
 * @roles admin, ops_manager (alle), prozess_owner (eigene Abteilung)
 * @i18n qmFeedback.*
 * @since v2.407.0
 */

import { useEffect, useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../context/LanguageContext';
import {
  MessageSquare, Mic, Smartphone, Search, Filter, Loader2, X, Check, AlertCircle,
  ThumbsUp, ThumbsDown, HelpCircle, Edit3, Clock, Image as ImageIcon, RefreshCw,
} from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';

const STATUS_BADGES = {
  offen:        { bg: '#3B82F618', color: '#3B82F6', dot: '#3B82F6' },
  in_pruefung:  { bg: '#F59E0B18', color: '#B45309', dot: '#F59E0B' },
  rueckfrage:   { bg: '#A855F718', color: '#7E22CE', dot: '#A855F7' },
  angenommen:   { bg: '#10B98118', color: '#047857', dot: '#10B981' },
  abgelehnt:    { bg: '#EF444418', color: '#B91C1C', dot: '#EF4444' },
};
const PRIO_BADGES = {
  high:   { bg: '#EF444418', color: '#B91C1C', label: 'High' },
  medium: { bg: '#F59E0B18', color: '#B45309', label: 'Medium' },
  low:    { bg: '#9CA3AF22', color: '#4B5563', label: 'Low' },
};

function StatusBadge({ status, t }) {
  const cfg = STATUS_BADGES[status] || STATUS_BADGES.offen;
  return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-semibold"
          style={{ backgroundColor: cfg.bg, color: cfg.color }}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: cfg.dot }} />
      {t(`qmFeedback.status.${status}`, status)}
    </span>
  );
}

function PrioBadge({ prio }) {
  const cfg = PRIO_BADGES[prio] || PRIO_BADGES.medium;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
          style={{ backgroundColor: cfg.bg, color: cfg.color }}>
      {cfg.label}
    </span>
  );
}

function ChannelIcon({ channel }) {
  if (channel === 'telegram') return <Mic size={14} className="text-[var(--text-tertiary,#6B7280)]" />;
  return <Smartphone size={14} className="text-[var(--text-tertiary,#6B7280)]" />;
}

function fmtDate(dt) {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleString('de-AT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function QmFeedbackPage() {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);

  // Filter
  const [filterStatus, setFilterStatus] = useState('');
  const [filterPrio, setFilterPrio] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterStatus) params.set('status', filterStatus);
      if (filterPrio) params.set('prioritaet', filterPrio);
      const [listRes, statsRes] = await Promise.all([
        fetch(`${API}/api/prozess-feedback?${params.toString()}`, { credentials: 'include' }),
        fetch(`${API}/api/prozess-feedback/stats`, { credentials: 'include' }),
      ]);
      if (listRes.ok) setItems(await listRes.json() || []);
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [filterStatus, filterPrio]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter(i =>
      (i.vorschlag_zusammenfassung || '').toLowerCase().includes(q) ||
      (i.prozess_titel || '').toLowerCase().includes(q) ||
      (`${i.ma_vorname || ''} ${i.ma_nachname || ''}`).toLowerCase().includes(q)
    );
  }, [items, search]);

  return (
    <div className="p-6 md:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <MessageSquare size={24} className="text-[var(--text-secondary,#4B5563)]" />
          <div>
            <h1 className="text-xl font-semibold text-[var(--text-primary,#1A1A2E)]">
              {t('qmFeedback.title', 'Prozess-Vorschlaege')}
            </h1>
            <p className="text-xs text-[var(--text-secondary,#4B5563)] mt-0.5">
              {t('qmFeedback.subtitle', 'Verbesserungs-Vorschlaege aus dem Telegram-Bot und MA-Portal')}
            </p>
          </div>
        </div>
        <button onClick={load}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-sm text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
          <RefreshCw size={14} />
          {t('common.refresh', 'Aktualisieren')}
        </button>
      </div>

      {/* KPI Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label={t('qmFeedback.kpi.gesamt', 'Gesamt')} value={stats.total} />
          <KpiCard label={t('qmFeedback.status.offen', 'Offen')} value={stats.offen} colorDot="#3B82F6" />
          <KpiCard label={t('qmFeedback.status.in_pruefung', 'In Pruefung')} value={stats.in_pruefung} colorDot="#F59E0B" />
          <KpiCard label={t('qmFeedback.status.angenommen', 'Angenommen')} value={stats.angenommen} colorDot="#10B981" />
          <KpiCard label={t('qmFeedback.status.abgelehnt', 'Abgelehnt')} value={stats.abgelehnt} colorDot="#EF4444" />
        </div>
      )}

      {/* Filter Bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-tertiary,#6B7280)]" />
          <input
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('qmFeedback.searchPlaceholder', 'Suchen nach Zusammenfassung, Prozess oder MA...')}
            className="w-full pl-9 pr-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm focus:outline-none focus:border-[#338edc]"
          />
        </div>
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm">
          <option value="">{t('qmFeedback.filter.allStatus', 'Alle Status')}</option>
          <option value="offen">{t('qmFeedback.status.offen', 'Offen')}</option>
          <option value="in_pruefung">{t('qmFeedback.status.in_pruefung', 'In Pruefung')}</option>
          <option value="rueckfrage">{t('qmFeedback.status.rueckfrage', 'Rueckfrage')}</option>
          <option value="angenommen">{t('qmFeedback.status.angenommen', 'Angenommen')}</option>
          <option value="abgelehnt">{t('qmFeedback.status.abgelehnt', 'Abgelehnt')}</option>
        </select>
        <select value={filterPrio} onChange={e => setFilterPrio(e.target.value)}
          className="px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm">
          <option value="">{t('qmFeedback.filter.allPrios', 'Alle Prioritaeten')}</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {/* Liste */}
      <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-[var(--text-tertiary,#6B7280)]">
            <Loader2 size={20} className="animate-spin inline-block mr-2" />
            {t('common.loading', 'Lade…')}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center text-[var(--text-tertiary,#6B7280)]">
            <MessageSquare size={28} className="mx-auto mb-3 opacity-50" />
            <p className="text-sm">{t('qmFeedback.empty', 'Keine Vorschlaege gefunden')}</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-inset,#F8F9FC)] text-[var(--text-tertiary,#6B7280)] text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.eingang', 'Eingang')}</th>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.kanal', 'Kanal')}</th>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.einreicher', 'Einreicher')}</th>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.prozess', 'Prozess')}</th>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.summary', 'Vorschlag')}</th>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.prio', 'Prio')}</th>
                <th className="text-left px-4 py-2.5 font-semibold">{t('qmFeedback.col.status', 'Status')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--border-card,#E5E7EB)]">
              {filtered.map(row => (
                <tr key={row.id} onClick={() => setActiveId(row.id)}
                    className="hover:bg-[var(--bg-hover,#FAFAFE)] cursor-pointer">
                  <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)] whitespace-nowrap">
                    {fmtDate(row.eingereicht_am)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <ChannelIcon channel={row.channel} />
                      <span className="text-[var(--text-secondary,#4B5563)] text-xs">{row.channel}</span>
                      {row.hat_audio && <Mic size={12} className="text-[#338edc]" />}
                      {row.hat_foto && <ImageIcon size={12} className="text-[#338edc]" />}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-[var(--text-primary,#1A1A2E)] whitespace-nowrap">
                    {row.ma_vorname ? `${row.ma_vorname} ${row.ma_nachname || ''}` : '—'}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-secondary,#4B5563)]">
                    {row.prozess_titel || <span className="italic text-[var(--text-tertiary,#6B7280)]">{t('qmFeedback.kein_prozess', 'unbekannt')}</span>}
                    {row.prozess_abteilung_name && (
                      <span className="text-[10px] text-[var(--text-tertiary,#6B7280)] block">{row.prozess_abteilung_name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[var(--text-primary,#1A1A2E)] max-w-[400px]">
                    <div className="line-clamp-2">{row.vorschlag_zusammenfassung || '—'}</div>
                  </td>
                  <td className="px-4 py-3"><PrioBadge prio={row.prioritaet} /></td>
                  <td className="px-4 py-3"><StatusBadge status={row.status} t={t} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail Modal */}
      {activeId && (
        <FeedbackDetailModal
          id={activeId}
          t={t}
          onClose={() => setActiveId(null)}
          onChanged={() => { setActiveId(null); load(); }}
        />
      )}
    </div>
  );
}

function KpiCard({ label, value, colorDot }) {
  return (
    <div className="rounded-2xl border border-[var(--border-card,#E5E7EB)] bg-[var(--bg-card,#fff)] p-4">
      <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-tertiary,#6B7280)] uppercase tracking-wider mb-1">
        {colorDot && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorDot }} />}
        {label}
      </div>
      <div className="text-2xl font-semibold text-[var(--text-primary,#1A1A2E)]">{value ?? 0}</div>
    </div>
  );
}

// ── Detail Modal ──────────────────────────────────────────────────────────
function FeedbackDetailModal({ id, t, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [edit, setEdit] = useState({});
  const [actionMode, setActionMode] = useState(null); // 'annehmen' | 'ablehnen' | 'rueckfrage'
  const [actionKommentar, setActionKommentar] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [prozesse, setProzesse] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/prozess-feedback/${id}`, { credentials: 'include' });
      if (r.ok) {
        const d = await r.json();
        setData(d.feedback);
        setAudit(d.audit || []);
        setEdit({
          prozess_id: d.feedback.prozess_id || '',
          schritt_nummer: d.feedback.schritt_nummer || '',
          vorschlag_zusammenfassung: d.feedback.vorschlag_zusammenfassung || '',
          prioritaet: d.feedback.prioritaet || 'medium',
        });
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [id]);

  useEffect(() => { load(); }, [load]);

  // Lazy-Load Prozesse fuer Strukturierungs-Edit
  useEffect(() => {
    if (!editMode || prozesse.length > 0) return;
    fetch(`${API}/api/prozesse?limit=200`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { prozesse: [] })
      .then(d => setProzesse(d.prozesse || d || []))
      .catch(() => {});
  }, [editMode, prozesse.length]);

  const saveEdit = async () => {
    setSaving(true); setError('');
    try {
      const body = {
        prozess_id: edit.prozess_id || null,
        schritt_nummer: edit.schritt_nummer || null,
        vorschlag_zusammenfassung: edit.vorschlag_zusammenfassung,
        prioritaet: edit.prioritaet,
      };
      const r = await fetch(`${API}/api/prozess-feedback/${id}/strukturierung`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(body),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || 'Error'); }
      else { setEditMode(false); await load(); }
    } catch { setError('Network error'); }
    setSaving(false);
  };

  const performAction = async () => {
    if ((actionMode === 'ablehnen' || actionMode === 'rueckfrage') && !actionKommentar.trim()) {
      setError(t('qmFeedback.error.kommentar_pflicht', 'Begruendung ist Pflicht'));
      return;
    }
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API}/api/prozess-feedback/${id}/${actionMode}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ kommentar: actionKommentar || null }),
      });
      if (!r.ok) { const d = await r.json().catch(() => ({})); setError(d.error || 'Error'); }
      else { onChanged(); }
    } catch { setError('Network error'); }
    setSaving(false);
  };

  const setInPruefung = async () => {
    setSaving(true); setError('');
    try {
      const r = await fetch(`${API}/api/prozess-feedback/${id}/in-pruefung`, {
        method: 'POST', credentials: 'include',
      });
      if (r.ok) await load();
    } catch { setError('Network error'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div className="bg-[var(--bg-card,#fff)] rounded-2xl border border-[var(--border-card,#E5E7EB)] w-full max-w-3xl mx-4 max-h-[92vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border-card,#E5E7EB)]">
          <div>
            <h2 className="text-base font-semibold text-[var(--text-primary,#1A1A2E)]">
              {t('qmFeedback.detail.title', 'Vorschlag #')}{id}
            </h2>
            {data && (
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={data.status} t={t} />
                <PrioBadge prio={data.prioritaet} />
                <span className="text-[11px] text-[var(--text-tertiary,#6B7280)]">{fmtDate(data.eingereicht_am)}</span>
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-[var(--text-tertiary,#6B7280)] hover:text-[var(--text-primary)]"><X size={18} /></button>
        </div>

        <div className="overflow-auto p-6 space-y-5">
          {loading ? (
            <div className="text-center py-8 text-[var(--text-tertiary,#6B7280)]">
              <Loader2 size={20} className="animate-spin inline mr-2" />
              {t('common.loading', 'Lade…')}
            </div>
          ) : data ? (
            <>
              {/* Einreicher */}
              <Section label={t('qmFeedback.detail.einreicher', 'Einreicher')}>
                <div className="text-sm text-[var(--text-primary,#1A1A2E)]">
                  {data.ma_vorname ? `${data.ma_vorname} ${data.ma_nachname || ''}` : '—'}
                  <span className="text-xs text-[var(--text-tertiary,#6B7280)] ml-2">via {data.channel}</span>
                </div>
              </Section>

              {/* Audio Player */}
              {data.audio_pfad && (
                <Section label={t('qmFeedback.detail.audio', 'Audio-Aufnahme')}>
                  <audio controls src={`${API}/api/prozess-feedback/${id}/audio`} className="w-full" />
                </Section>
              )}

              {/* Foto */}
              {data.foto_pfad && (
                <Section label={t('qmFeedback.detail.foto', 'Foto')}>
                  <img src={`${API}/api/prozess-feedback/${id}/foto`} alt="" className="rounded-[10px] border border-[var(--border-card,#E5E7EB)] max-h-64" />
                </Section>
              )}

              {/* Whisper-Transkript */}
              {data.whisper_transkript && (
                <Section label={t('qmFeedback.detail.transkript', 'Transkript / Originaltext')}>
                  <div className="text-sm text-[var(--text-primary,#1A1A2E)] whitespace-pre-wrap bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-3 border border-[var(--border-card,#E5E7EB)]">
                    {data.whisper_transkript}
                  </div>
                </Section>
              )}

              {/* Sonnet-Strukturierung */}
              <Section label={t('qmFeedback.detail.struktur', 'Sonnet-Strukturierung')}
                       action={!editMode && data.status !== 'angenommen' && data.status !== 'abgelehnt' && (
                         <button onClick={() => setEditMode(true)}
                           className="text-xs text-[#338edc] hover:underline inline-flex items-center gap-1">
                           <Edit3 size={12} /> {t('common.edit', 'Bearbeiten')}
                         </button>
                       )}>
                {editMode ? (
                  <div className="space-y-3">
                    <Field label={t('qmFeedback.field.prozess', 'Prozess')}>
                      <select value={edit.prozess_id || ''} onChange={e => setEdit({ ...edit, prozess_id: e.target.value || '' })}
                        className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm">
                        <option value="">— {t('qmFeedback.kein_prozess', 'unbekannt')} —</option>
                        {prozesse.map(p => (
                          <option key={p.id} value={p.id}>{p.titel}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label={t('qmFeedback.field.schritt', 'Betroffener Schritt')}>
                      <input type="number" value={edit.schritt_nummer || ''} onChange={e => setEdit({ ...edit, schritt_nummer: e.target.value })}
                        className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm" />
                    </Field>
                    <Field label={t('qmFeedback.field.summary', 'Zusammenfassung')}>
                      <textarea value={edit.vorschlag_zusammenfassung || ''} onChange={e => setEdit({ ...edit, vorschlag_zusammenfassung: e.target.value })}
                        rows={2}
                        className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm resize-none" />
                    </Field>
                    <Field label={t('qmFeedback.field.prio', 'Prioritaet')}>
                      <select value={edit.prioritaet} onChange={e => setEdit({ ...edit, prioritaet: e.target.value })}
                        className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm">
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </Field>
                    <div className="flex justify-end gap-2 pt-1">
                      <button onClick={() => setEditMode(false)} disabled={saving}
                        className="px-3 py-1.5 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
                        {t('common.cancel', 'Abbrechen')}
                      </button>
                      <button onClick={saveEdit} disabled={saving}
                        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold">
                        {saving && <Loader2 size={12} className="animate-spin" />}
                        {t('common.save', 'Speichern')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <dl className="text-sm space-y-1.5">
                    <Row label={t('qmFeedback.field.prozess', 'Prozess')} value={data.prozess_titel || <em className="text-[var(--text-tertiary,#6B7280)]">{t('qmFeedback.kein_prozess', 'unbekannt')}</em>} />
                    {data.schritt_nummer && <Row label={t('qmFeedback.field.schritt', 'Schritt')} value={data.schritt_nummer} />}
                    <Row label={t('qmFeedback.field.summary', 'Zusammenfassung')} value={data.vorschlag_zusammenfassung} />
                    {data.sonnet_strukturiert?.konfidenz != null && (
                      <Row label={t('qmFeedback.field.konfidenz', 'Konfidenz')} value={`${Math.round(data.sonnet_strukturiert.konfidenz * 100)}%`} />
                    )}
                  </dl>
                )}
              </Section>

              {/* Bearbeitung Info */}
              {data.bearbeitet_am && (
                <Section label={t('qmFeedback.detail.bearbeitet', 'Bearbeitung')}>
                  <div className="text-sm text-[var(--text-secondary,#4B5563)]">
                    <div>{fmtDate(data.bearbeitet_am)} · {data.bearbeiter_vorname} {data.bearbeiter_nachname}</div>
                    {data.resultat_kommentar && (
                      <div className="mt-2 p-3 rounded-[10px] bg-[var(--bg-inset,#F8F9FC)] border border-[var(--border-card,#E5E7EB)] text-[var(--text-primary,#1A1A2E)]">
                        {data.resultat_kommentar}
                      </div>
                    )}
                  </div>
                </Section>
              )}

              {/* Audit */}
              {audit.length > 0 && (
                <Section label={t('qmFeedback.detail.audit', 'Audit-Verlauf')}>
                  <ul className="text-xs space-y-1.5">
                    {audit.map(a => (
                      <li key={a.id} className="flex items-start gap-2">
                        <Clock size={10} className="mt-0.5 text-[var(--text-tertiary,#6B7280)] flex-shrink-0" />
                        <div>
                          <span className="text-[var(--text-tertiary,#6B7280)]">{fmtDate(a.created_at)} · </span>
                          <span className="font-semibold text-[var(--text-primary,#1A1A2E)]">{a.aktion}</span>
                          {a.vorname && <span className="text-[var(--text-secondary,#4B5563)]"> · {a.vorname} {a.nachname}</span>}
                          {a.kommentar && <div className="text-[var(--text-secondary,#4B5563)] italic">„{a.kommentar}"</div>}
                        </div>
                      </li>
                    ))}
                  </ul>
                </Section>
              )}

              {/* Aktion-Modal */}
              {actionMode && (
                <div className="rounded-2xl border border-[#338edc] bg-[#338edc08] p-4 space-y-2">
                  <div className="text-sm font-semibold text-[var(--text-primary,#1A1A2E)]">
                    {actionMode === 'annehmen' && t('qmFeedback.action.annehmen_title', 'Vorschlag annehmen')}
                    {actionMode === 'ablehnen' && t('qmFeedback.action.ablehnen_title', 'Vorschlag ablehnen')}
                    {actionMode === 'rueckfrage' && t('qmFeedback.action.rueckfrage_title', 'Rueckfrage stellen')}
                  </div>
                  <textarea value={actionKommentar} onChange={e => setActionKommentar(e.target.value)}
                    rows={3}
                    placeholder={
                      actionMode === 'annehmen'
                        ? t('qmFeedback.action.annehmen_hint', 'Optional: Kommentar / Hinweis fuer den Einreicher')
                        : actionMode === 'ablehnen'
                          ? t('qmFeedback.action.ablehnen_hint', 'Begruendung (Pflicht)')
                          : t('qmFeedback.action.rueckfrage_hint', 'Frage an den Einreicher (Pflicht)')
                    }
                    className="w-full px-3 py-2 rounded-[10px] border border-[var(--border-input,#E5E7EB)] bg-[var(--bg-input,#fff)] text-sm resize-none" />
                  {error && <div className="text-xs text-[#EF4444]">{error}</div>}
                  <div className="flex justify-end gap-2">
                    <button onClick={() => { setActionMode(null); setActionKommentar(''); setError(''); }} disabled={saving}
                      className="px-3 py-1.5 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-hover,#FAFAFE)]">
                      {t('common.cancel', 'Abbrechen')}
                    </button>
                    <button onClick={performAction} disabled={saving}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-[#338edc] hover:bg-[#2b7bc4] text-white text-xs font-semibold">
                      {saving && <Loader2 size={12} className="animate-spin" />}
                      {t('common.confirm', 'Bestaetigen')}
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8 text-[#EF4444]">{t('qmFeedback.detail.not_found', 'Nicht gefunden')}</div>
          )}
        </div>

        {/* Footer Actions */}
        {data && !actionMode && data.status !== 'angenommen' && data.status !== 'abgelehnt' && (
          <div className="px-6 py-3 border-t border-[var(--border-card,#E5E7EB)] flex flex-wrap gap-2 justify-end bg-[var(--bg-inset,#F8F9FC)]">
            {data.status === 'offen' && (
              <button onClick={setInPruefung} disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-[var(--border-card,#E5E7EB)] text-xs text-[var(--text-secondary,#4B5563)] hover:bg-[var(--bg-card,#fff)]">
                <AlertCircle size={12} /> {t('qmFeedback.action.in_pruefung', 'In Pruefung nehmen')}
              </button>
            )}
            <button onClick={() => { setActionMode('rueckfrage'); setActionKommentar(''); setError(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-[#A855F7] text-xs text-[#7E22CE] hover:bg-[#A855F718]">
              <HelpCircle size={12} /> {t('qmFeedback.action.rueckfrage', 'Rueckfrage')}
            </button>
            <button onClick={() => { setActionMode('ablehnen'); setActionKommentar(''); setError(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] border border-[#EF4444] text-xs text-[#B91C1C] hover:bg-[#EF444418]">
              <ThumbsDown size={12} /> {t('qmFeedback.action.ablehnen', 'Ablehnen')}
            </button>
            <button onClick={() => { setActionMode('annehmen'); setActionKommentar(''); setError(''); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[10px] bg-[#10B981] hover:bg-[#059669] text-xs text-white font-semibold">
              <ThumbsUp size={12} /> {t('qmFeedback.action.annehmen', 'Annehmen')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function Section({ label, children, action }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[11px] uppercase tracking-wider font-semibold text-[var(--text-tertiary,#6B7280)]">
          {label}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[11px] font-semibold text-[var(--text-secondary,#4B5563)] mb-1">{label}</div>
      {children}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex gap-2">
      <dt className="w-32 text-[var(--text-tertiary,#6B7280)] flex-shrink-0">{label}</dt>
      <dd className="text-[var(--text-primary,#1A1A2E)] flex-1">{value || '—'}</dd>
    </div>
  );
}
